import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { TrackMode } from '@fitness-force/enum';
import { environment } from '@fitness-force/environments';
import { AppErrors, NotFoundError } from '@fitness-force/models';
import type {
  PosTerminal,
  PosTerminalDeactivateResponse,
  PosTerminalRegisterRequest,
  PosTerminalRegisterResponse,
} from '@fitness-force/models';
import { Observable, catchError, map, throwError } from 'rxjs';

export interface PosTerminalListParams {
  active_only?: boolean;
  pgwconfmst_id?: number;
}

@Injectable({
  providedIn: 'root',
})
export class PosTerminalService {
  private readonly http = inject(HttpClient);

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  getPosTerminals(
    tenantId: number,
    params?: PosTerminalListParams
  ): Observable<PosTerminal[]> {
    let httpParams = new HttpParams();
    if (params?.active_only !== undefined) {
      httpParams = httpParams.set('active_only', String(params.active_only));
    }
    if (params?.pgwconfmst_id !== undefined) {
      httpParams = httpParams.set('pgwconfmst_id', params.pgwconfmst_id.toString());
    }

    const url =
      environment.BASE_URL +
      CONST_URL_SEGEMENTS.PAYMENT.POS_TERMINAL_LIST.replace(
        '{tenant_id}',
        tenantId.toString()
      );

    return this.http
      .get<PosTerminal[]>(url, {
        headers: this.httpOptions.headers,
        params: httpParams,
      })
      .pipe(
        map((res) => res ?? []),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  registerPosTerminal(
    tenantId: number,
    body: PosTerminalRegisterRequest
  ): Observable<PosTerminalRegisterResponse> {
    const url =
      environment.BASE_URL +
      CONST_URL_SEGEMENTS.PAYMENT.POS_TERMINAL_REGISTER.replace(
        '{tenant_id}',
        tenantId.toString()
      );

    return this.http
      .post<PosTerminalRegisterResponse>(url, body, this.httpOptions)
      .pipe(
        map((res) => res),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getPosTerminalById(tenantId: number, id: number): Observable<PosTerminal> {
    const url =
      environment.BASE_URL +
      CONST_URL_SEGEMENTS.PAYMENT.POS_TERMINAL_GET_BY_ID.replace(
        '{tenant_id}',
        tenantId.toString()
      ).replace('{id}', id.toString());

    return this.http.get<PosTerminal>(url, this.httpOptions).pipe(
      map((res) => res),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  deactivatePosTerminal(
    tenantId: number,
    id: number,
    trackMode: TrackMode = TrackMode.StaffWebApp
  ): Observable<PosTerminalDeactivateResponse> {
    const url =
      environment.BASE_URL +
      CONST_URL_SEGEMENTS.PAYMENT.POS_TERMINAL_DEACTIVATE.replace(
        '{tenant_id}',
        tenantId.toString()
      ).replace('{id}', id.toString());

    return this.http
      .patch<PosTerminalDeactivateResponse>(
        url,
        { track_mode: trackMode },
        this.httpOptions
      )
      .pipe(
        map((res) => res),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }
}
